import { useMemo, useCallback } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  MarkerType,
} from '@xyflow/react';
import dagre from 'dagre';
import WorkOrderNode from './WorkOrderNode';
import { IWorkOrder } from '@/types/mes';
import '@xyflow/react/dist/style.css';

interface ManufacturingFlowViewProps {
  workOrders: IWorkOrder[];
  onWorkOrderClick: (woId: string) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const nodeTypes: any = {
  workOrderNode: WorkOrderNode,
};

const NODE_WIDTH = 220;
const NODE_HEIGHT = 180;

// Use dagre to auto-layout the graph in top-to-bottom direction for BOM hierarchy
const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  direction: 'TB' | 'LR' = 'TB'
) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  dagreGraph.setGraph({ rankdir: direction, nodesep: 60, ranksep: 80 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

const ManufacturingFlowView = ({
  workOrders,
  onWorkOrderClick,
}: ManufacturingFlowViewProps) => {
  // Build nodes and edges from work orders
  const { initialNodes, initialEdges } = useMemo(() => {
    const nodes: Node[] = workOrders.map((wo) => ({
      id: wo.id,
      type: 'workOrderNode',
      data: { workOrder: wo, onNodeClick: onWorkOrderClick },
      position: { x: 0, y: 0 }, // Will be set by dagre
      draggable: false,
    }));

    const edges: Edge[] = [];
    const woIds = new Set(workOrders.map((wo) => wo.id));

    workOrders.forEach((wo) => {
      // Create edge from parent to this work order (hierarchical BOM structure)
      if (wo.parent_wo_id && woIds.has(wo.parent_wo_id)) {
        edges.push({
          id: `${wo.parent_wo_id}-${wo.id}`,
          source: wo.parent_wo_id,
          target: wo.id,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
          },
          style: { strokeWidth: 2 },
          // Animate edges for work orders that are in progress
          animated: wo.status === 'in_progress',
        });
      }
    });

    // Apply dagre layout (top-to-bottom for BOM hierarchy)
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodes,
      edges,
      'TB'
    );

    return { initialNodes: layoutedNodes, initialEdges: layoutedEdges };
  }, [workOrders, onWorkOrderClick]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes/edges when work orders change
  useMemo(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      initialNodes,
      initialEdges,
      'TB'
    );
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      onWorkOrderClick(node.id);
    },
    [onWorkOrderClick]
  );

  // Get node color for MiniMap based on assembly type
  const getNodeColor = (node: Node) => {
    const wo = (node.data as { workOrder: IWorkOrder }).workOrder;
    switch (wo.assembly_type) {
      case 'pcba':
        return '#3b82f6'; // blue
      case 'module':
        return '#f97316'; // orange
      case 'product':
        return '#22c55e'; // green
      default:
        return '#9ca3af';
    }
  };

  return (
    <div className="h-[600px] bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.3}
        maxZoom={1.5}
        attributionPosition="bottom-left"
      >
        <Background color="#94a3b8" gap={20} size={1} />
        <Controls className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" />
        <MiniMap
          nodeColor={getNodeColor}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
        />
      </ReactFlow>

      {/* Assembly Type Legend */}
      <div className="absolute bottom-4 left-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg z-10">
        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Assembly Types
        </h4>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-500"></div>
            <span className="text-xs text-gray-600 dark:text-gray-400">
              PCB Assembly
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-orange-500"></div>
            <span className="text-xs text-gray-600 dark:text-gray-400">
              Module Assembly
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-green-500"></div>
            <span className="text-xs text-gray-600 dark:text-gray-400">
              Product Assembly
            </span>
          </div>
        </div>
      </div>

      {/* WO Status Legend */}
      <div className="absolute bottom-4 left-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg z-10">
        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Status
        </h4>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-gray-400"></div>
            <span className="text-xs text-gray-600 dark:text-gray-400">
              Pending/Ready
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-yellow-500"></div>
            <span className="text-xs text-gray-600 dark:text-gray-400">
              In Progress
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-green-500"></div>
            <span className="text-xs text-gray-600 dark:text-gray-400">
              Completed
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManufacturingFlowView;
